import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tree } from 'antd'
import {
  debounce,
  filter,
  find,
  flatten,
  get,
  groupBy,
  includes,
  intersection,
  isEmpty,
  isEqual,
  isNil,
  map,
  size,
  uniq,
} from 'lodash'
import PropTypes from 'prop-types'

import { usePrevious } from '../../../core/hooks'

import { INGREDIENT_TREE_FIELD_MIN_HEIGHT } from '../../constants'
import {
  filterParentKeysWhenNotAllChildrenSelected,
  filterTree,
} from '../../helpers'
import { idPropType } from '../../prop-types'
import {
  flattenTreeStructure,
  getDefaultExpandedKeys,
  stringIncludes,
} from '../../utils'
import { StyledIngredientKeywordsColumn } from '../styled'

import IngredientKeywordNodeTitle from './ingredient-keyword-node-title'

const IngredientKeywordTree = ({
  treeData,
  keywordsType,
  editable,
  checkable,
  onAdd,
  onRename,
  onCheck,
  setDataForRenaming,
  dataForRenaming,
  languageOptions,
  checkedKeys,
  checkStrictly,
  languageIdsFilter,
  searchValue,
}) => {
  const treeInstance = useRef()
  const [searchableTreeData, setSearchableTreeData] = useState(treeData)

  const [treeExpandedKeys, setTreeExpandedKeys] = useState(
    getDefaultExpandedKeys(treeData),
  )

  const flatTreeData = useMemo(() => flattenTreeStructure(treeData), [treeData])

  const languageOptionsGroupedByValue = useMemo(
    () => groupBy(languageOptions, 'value'),
    [languageOptions],
  )

  const isAllExpanded = useMemo(
    () => size(treeExpandedKeys) === size(flatTreeData),
    [treeExpandedKeys, flatTreeData],
  )

  const filterTreeDataByLanguageIds = useCallback(
    languageIdsFilter => {
      const filteredBySearchValueTreeData = filterTree(
        treeData,
        node =>
          isEmpty(languageIdsFilter) ||
          includes(languageIdsFilter, node.languageId),
      )

      if (!isEmpty(languageIdsFilter)) {
        const nodesToExpand = flatten(
          map(
            filter(flatTreeData, node =>
              includes(languageIdsFilter, node.languageId),
            ),
            'keyPath',
          ),
        )

        setTreeExpandedKeys(nodesToExpand)
      }

      setSearchableTreeData(filteredBySearchValueTreeData)
    },
    [flatTreeData, treeData],
  )

  const handleDebouncedFiltreSearchableTree = useMemo(
    () =>
      debounce(value => {
        const filteredBySearchValueTreeData = filterTree(treeData, node => {
          if (stringIncludes(value, "'")) {
            return (
              stringIncludes(node.term, value) ||
              stringIncludes(node.term, value.replaceAll("'", '’'))
            )
          }

          return stringIncludes(node.term, value)
        })

        if (!isEmpty(value)) {
          const filteredNodesBySearchValue = filter(flatTreeData, node => {
            if (stringIncludes(value, "'")) {
              return (
                stringIncludes(node.term, value) ||
                stringIncludes(node.term, value.replaceAll("'", '’'))
              )
            }

            return stringIncludes(node.term, value)
          })

          const nodesToExpand = flatten(
            map(filteredNodesBySearchValue, 'keyPath'),
          )

          setTreeExpandedKeys(nodesToExpand)
        }

        setSearchableTreeData(filteredBySearchValueTreeData)
      }, 300),
    [treeData, flatTreeData],
  )

  const treeNodeToEdit = useMemo(
    () => find(flatTreeData, ({ id }) => id === dataForRenaming?.id),
    [flatTreeData, dataForRenaming?.id],
  )

  const handleTreeOnExpand = useCallback(expandedKeys => {
    setTreeExpandedKeys(expandedKeys)
  }, [])

  const toggleTreeExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setTreeExpandedKeys([])
    } else {
      setTreeExpandedKeys(map(flatTreeData, 'key'))
    }
  }, [flatTreeData, isAllExpanded])

  const handleTreeOnCheck = useCallback(
    ({ checked }) => {
      if (checkStrictly) {
        return onCheck(checked, keywordsType.field)
      }

      if (size(checked) < size(checkedKeys)) {
        const keysToCheck = filterParentKeysWhenNotAllChildrenSelected(
          checked,
          flatTreeData,
        )

        return onCheck(keysToCheck, keywordsType.field)
      }

      const nodesToCheck = filter(
        flatTreeData,
        ({ key, keyPath }) =>
          includes(checked, key) || !isEmpty(intersection(checked, keyPath)),
      )

      return onCheck(map(nodesToCheck, 'key'), keywordsType.field)
    },
    [checkStrictly, checkedKeys, flatTreeData, keywordsType.field, onCheck],
  )

  const handleOnAddChildNode = useCallback(
    node => {
      onAdd(node, keywordsType.field)

      setTreeExpandedKeys(previousExpandedKeys =>
        uniq([...previousExpandedKeys, ...node.keyPath]),
      )
    },
    [keywordsType, onAdd],
  )

  const handleOnRenameNode = useCallback(
    node => {
      onRename(node, keywordsType.field)
    },
    [keywordsType, onRename],
  )

  const titleRender = useCallback(
    node => {
      const languageName = get(
        languageOptionsGroupedByValue,
        `${node.languageId}[0].label`,
      )

      return (
        <IngredientKeywordNodeTitle
          editable={editable}
          node={node}
          languageName={languageName}
          onAdd={handleOnAddChildNode}
          onRename={handleOnRenameNode}
          setDataForRenaming={setDataForRenaming}
          showRenameInput={dataForRenaming?.id === node.id}
          isAllExpanded={isAllExpanded}
          toggleTreeExpandAll={toggleTreeExpandAll}
          searchValue={searchValue}
          dataForRenaming={dataForRenaming}
        />
      )
    },
    [
      editable,
      handleOnAddChildNode,
      handleOnRenameNode,
      dataForRenaming,
      isAllExpanded,
      languageOptionsGroupedByValue,
      searchValue,
      setDataForRenaming,
      toggleTreeExpandAll,
    ],
  )

  useEffect(() => {
    handleDebouncedFiltreSearchableTree(searchValue)
  }, [handleDebouncedFiltreSearchableTree, searchValue])

  useEffect(() => {
    filterTreeDataByLanguageIds(languageIdsFilter)
  }, [filterTreeDataByLanguageIds, languageIdsFilter])

  const previousSearchValue = usePrevious(searchValue)

  useEffect(() => {
    if (
      isEmpty(searchValue) &&
      !isEmpty(previousSearchValue) &&
      !isEqual(treeExpandedKeys, getDefaultExpandedKeys(treeData))
    ) {
      setTreeExpandedKeys(getDefaultExpandedKeys(treeData))
    }
  }, [
    keywordsType,
    previousSearchValue,
    searchValue,
    treeData,
    treeExpandedKeys,
  ])

  useEffect(() => {
    let scrollToTimeout

    if (
      !isNil(treeNodeToEdit) &&
      includes(treeExpandedKeys, treeNodeToEdit.key)
    ) {
      scrollToTimeout = setTimeout(() => {
        treeInstance.current.scrollTo({
          key: treeNodeToEdit.key,
          align: 'middle',
        })
      }, 500)
    }

    return () => clearTimeout(scrollToTimeout)
  }, [dataForRenaming?.id, treeExpandedKeys, treeNodeToEdit])

  const previousCheckable = usePrevious(checkable)
  const [isCheckable, setIsCheckable] = useState(false)

  // hack to reset the tree checks when changing checkable
  useEffect(() => {
    if (previousCheckable && !checkable) {
      onCheck([], keywordsType.field)
    }
  }, [checkable, keywordsType, onCheck, previousCheckable])

  useEffect(() => {
    if (!checkable && isEmpty(checkedKeys)) {
      setIsCheckable(false)
    }
  }, [checkable, checkedKeys, keywordsType])

  useEffect(() => {
    if (checkable) {
      setIsCheckable(true)
    }
  }, [checkable])

  return (
    <StyledIngredientKeywordsColumn span={6}>
      <Tree
        ref={treeInstance}
        height={INGREDIENT_TREE_FIELD_MIN_HEIGHT}
        checkable={isCheckable}
        checkStrictly
        checkedKeys={checkedKeys}
        onCheck={handleTreeOnCheck}
        onExpand={handleTreeOnExpand}
        expandedKeys={treeExpandedKeys}
        treeData={searchableTreeData}
        titleRender={titleRender}
      />
    </StyledIngredientKeywordsColumn>
  )
}

IngredientKeywordTree.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.object).isRequired,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  keywordsType: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  checkable: PropTypes.bool,
  onAdd: PropTypes.func,
  onRename: PropTypes.func,
  onCheck: PropTypes.func,
  checkStrictly: PropTypes.bool,
  setDataForRenaming: PropTypes.func,
  dataForRenaming: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  checkedKeys: PropTypes.arrayOf(PropTypes.string),
  languageIdsFilter: PropTypes.arrayOf(idPropType),
  searchValue: PropTypes.string,
}

IngredientKeywordTree.defaultProps = {
  editable: false,
  checkable: false,
  checkStrictly: false,
  onAdd: undefined,
  onRename: undefined,
  onCheck: undefined,
  setDataForRenaming: undefined,
  dataForRenaming: undefined,
  checkedKeys: [],
  languageIdsFilter: [],
  searchValue: undefined,
}

export default IngredientKeywordTree
